import React, { useState, useEffect } from 'react';
import {
  Users, Clock, CheckCircle, AlertCircle, ChevronRight, Search,
  Building, Phone, Globe, Calendar, MessageSquare, Shield, AlertTriangle,
  Eye, Check, X, Play, RefreshCw, Filter, Loader2, UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OpsOnboardingPage = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState('pending');

  const getOpsToken = () => localStorage.getItem('ops_token');

  useEffect(() => {
    loadQueue();
  }, [filter]);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/ops/queue?status=${filter}`, {
        headers: { 'X-Ops-Token': getOpsToken() }
      });
      if (res.ok) {
        const data = await res.json();
        setQueue(data.queue || []);
      }
    } catch (err) {
      console.error('Failed to load queue:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionDetail = async (clientId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/ops/submission/${clientId}`, {
        headers: { 'X-Ops-Token': getOpsToken() }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedItem(data);
      }
    } catch (err) {
      console.error('Failed to load detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const assignToSelf = async (queueId) => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/ops/queue/${queueId}/assign`, {
        method: 'POST',
        headers: { 'X-Ops-Token': getOpsToken() }
      });
      if (res.ok) {
        loadQueue();
      }
    } catch (err) {
      console.error('Failed to assign:', err);
    }
  };

  const markComplete = async (queueId) => {
    const notes = prompt('Enter completion notes (what was configured in SigmaMind):');
    if (!notes) return;

    try {
      const res = await fetch(`${API_URL}/api/onboarding/ops/queue/${queueId}/complete?notes=${encodeURIComponent(notes)}`, {
        method: 'POST',
        headers: { 'X-Ops-Token': getOpsToken() }
      });
      if (res.ok) {
        alert('Marked as complete!');
        loadQueue();
        setSelectedItem(null);
      }
    } catch (err) {
      alert('Failed to complete');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'completed': 'bg-green-100 text-green-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles['pending']}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">HIGH</span>;
    }
    return null;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onboarding Queue</h1>
          <p className="text-gray-500">Build AI agents from customer submissions</p>
        </div>
        <button
          onClick={loadQueue}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {queue.filter(q => q.status === 'pending').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">
                {queue.filter(q => q.status === 'in_progress').length}
              </p>
            </div>
            <Play className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {queue.filter(q => q.status === 'completed').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4 mb-6">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="all">All</option>
        </select>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Queue List */}
        <div className="col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">Queue ({queue.length})</h2>
          </div>
          <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : queue.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                No items in queue
              </div>
            ) : (
              queue.map(item => (
                <div
                  key={item.id}
                  onClick={() => loadSubmissionDetail(item.client_id)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedItem?.wizard?.client_id === item.client_id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{item.client_name}</span>
                    <div className="flex items-center gap-2">
                      {getPriorityBadge(item.priority)}
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 capitalize">
                    {item.industry?.replace('_', ' ')} • {item.industry_subcategory}
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    {item.assigned_to && (
                      <span className="flex items-center gap-1">
                        <UserCheck className="w-3 h-3" />
                        {item.assigned_to}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Detail View */}
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {detailLoading ? (
            <div className="p-8 text-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading details...
            </div>
          ) : selectedItem ? (
            <OpsSubmissionDetail
              data={selectedItem}
              queueItem={queue.find(q => q.client_id === selectedItem.wizard?.client_id)}
              onAssign={assignToSelf}
              onComplete={markComplete}
            />
          ) : (
            <div className="p-8 text-center text-gray-400">
              <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Select an item to view customer setup data</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OpsSubmissionDetail = ({ data, queueItem, onAssign, onComplete }) => {
  const { wizard, client, industries, call_objectives, tone_options } = data;
  
  if (!wizard) return null;

  const industry = industries?.[wizard.industry_selection?.primary_industry];
  const tone = tone_options?.find(t => t.id === wizard.brand_voice?.tone);

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{wizard.client_name || client?.name}</h2>
          <p className="text-sm text-gray-500">{client?.email} • {client?.plan_id} plan</p>
        </div>
        <div className="flex items-center gap-2">
          {queueItem?.status === 'pending' && (
            <button
              onClick={() => onAssign(queueItem.id)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <UserCheck className="w-4 h-4" />
              Assign to Me
            </button>
          )}
          {queueItem?.status === 'in_progress' && (
            <button
              onClick={() => onComplete(queueItem.id)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4" />
              Mark Complete
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Quick Summary for SigmaMind */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-3">🤖 SigmaMind Configuration Summary</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-blue-700 font-medium">Business:</span>
              <span className="text-blue-900 ml-2">{wizard.business_profile?.display_name}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Industry:</span>
              <span className="text-blue-900 ml-2">{wizard.industry_selection?.secondary_industry}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Tone:</span>
              <span className="text-blue-900 ml-2 capitalize">{wizard.brand_voice?.tone}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Booking:</span>
              <span className="text-blue-900 ml-2">{wizard.ai_permissions?.can_book_appointments ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <div className="mt-3 p-3 bg-white rounded border border-blue-200">
            <span className="text-blue-700 font-medium text-sm">Greeting:</span>
            <p className="text-blue-900 text-sm mt-1">"{wizard.brand_voice?.greeting_message}"</p>
          </div>
        </div>

        {/* Business Profile */}
        {wizard.business_profile && (
          <DetailSection title="Business Profile" icon={Building}>
            <DetailGrid>
              <DetailItem label="Legal Name" value={wizard.business_profile.legal_name} />
              <DetailItem label="Display Name" value={wizard.business_profile.display_name} />
              <DetailItem label="Phone" value={wizard.business_profile.primary_phone} />
              <DetailItem label="Website" value={wizard.business_profile.website} />
              <DetailItem label="Location" value={`${wizard.business_profile.city}, ${wizard.business_profile.state}`} />
              <DetailItem label="Timezone" value={wizard.business_profile.timezone} />
            </DetailGrid>
            {wizard.business_profile.business_hours && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Business Hours</h4>
                <div className="grid grid-cols-7 gap-2 text-sm">
                  {Object.entries(wizard.business_profile.business_hours).map(([day, hours]) => (
                    <div key={day} className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-gray-500 capitalize font-medium">{day.slice(0, 3)}</div>
                      <div className={`text-xs ${hours.closed ? 'text-red-500' : 'text-green-600'}`}>
                        {hours.closed ? 'Closed' : `${hours.open}-${hours.close}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DetailSection>
        )}

        {/* Call Objectives */}
        {wizard.call_objectives?.objectives && (
          <DetailSection title="Call Objectives" icon={Phone}>
            <div className="flex flex-wrap gap-2">
              {wizard.call_objectives.objectives.map(objId => {
                const obj = call_objectives?.find(o => o.id === objId);
                return (
                  <span key={objId} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {obj?.icon} {obj?.label || objId}
                  </span>
                );
              })}
            </div>
          </DetailSection>
        )}

        {/* AI Permissions */}
        {wizard.ai_permissions && (
          <DetailSection title="AI Permissions" icon={Shield}>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(wizard.ai_permissions).map(([key, value]) => {
                if (key === 'price_disclaimer') return null;
                return (
                  <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-700 capitalize">{key.replace(/_/g, ' ')}</span>
                    {value ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                );
              })}
            </div>
          </DetailSection>
        )}

        {/* Emergency Handling */}
        {wizard.emergency_handling && (
          <DetailSection title="Emergency & Edge Cases" icon={AlertTriangle}>
            <DetailGrid>
              <DetailItem label="Emergency Definition" value={wizard.emergency_handling.emergency_definition} full />
              <DetailItem label="Emergency Phone" value={wizard.emergency_handling.emergency_phone} />
              <DetailItem label="Fallback Message" value={wizard.emergency_handling.fallback_message} full />
              <DetailItem label="Outside Hours" value={wizard.emergency_handling.outside_hours_message} full />
              <DetailItem label="Fully Booked" value={wizard.emergency_handling.fully_booked_message} full />
            </DetailGrid>
          </DetailSection>
        )}

        {/* Brand Voice */}
        {wizard.brand_voice && (
          <DetailSection title="Brand Voice" icon={MessageSquare}>
            <DetailGrid>
              <DetailItem label="Tone" value={tone?.label || wizard.brand_voice.tone} />
              <DetailItem label="Greeting Message" value={wizard.brand_voice.greeting_message} full />
            </DetailGrid>
            {wizard.brand_voice.forbidden_phrases?.length > 0 && (
              <div className="mt-3">
                <span className="text-sm text-gray-600 font-medium">Forbidden Phrases:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {wizard.brand_voice.forbidden_phrases.map((phrase, idx) => (
                    <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm">
                      {phrase}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </DetailSection>
        )}
      </div>
    </div>
  );
};

const DetailSection = ({ title, icon: Icon, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-900 flex items-center gap-2">
      <Icon className="w-4 h-4 text-blue-600" />
      {title}
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
);

const DetailGrid = ({ children }) => (
  <div className="grid grid-cols-2 gap-3">
    {children}
  </div>
);

const DetailItem = ({ label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <span className="text-xs text-gray-500 block">{label}</span>
    <span className="text-sm text-gray-900">{value || '—'}</span>
  </div>
);

export default OpsOnboardingPage;
